import type { PortfolioFilters, PortfolioFiltersFormType, PortfolioType } from "~/types/Portfolio";
import { endpoints } from "~/constants/endpoints";

export function usePortfolio(initialSpecificationId: string) {
    const route = useRoute();
    const globalStore = useGlobalStore();
    const userStore = useUserStore();
    const { specifications } = globalStore;

    const isLoaded = ref(false);
    const mastersList = ref<PortfolioType[]>([]);
    const total = ref(0);
    const filtersList = ref<PortfolioFilters | null>(null);

    const filterForm = ref<PortfolioFiltersFormType>({
        specificationId: [initialSpecificationId],
        city: route.query.city ? (Array.isArray(route.query.city) ? route.query.city.filter(Boolean) as string[] : route.query.city.split('|')) : [],
        educationType: route.query.educationType ? (Array.isArray(route.query.educationType) ? route.query.educationType.filter(Boolean) as string[] : route.query.educationType.split('|')) : [],
        employmentType: route.query.employmentType ? (Array.isArray(route.query.employmentType) ? route.query.employmentType.filter(Boolean) as string[] : route.query.employmentType.split('|')) : [],
        isRemote: null,
        minLat: null,
        maxLat: null,
        minLng: null,
        maxLng: null,
        priceFrom: route.query.priceFrom ? Number(route.query.priceFrom) : null,
        priceTo: route.query.priceTo ? Number(route.query.priceTo) : null,
        pageSize: 10,
        page: route.query.page ? Number(route.query.page) : 1,
        reviewsCount: null,
        experienceFrom: route.query.experienceFrom ? Number(route.query.experienceFrom) : null,
        isAvailableToday: false,
        withRatingOverFour: false
    });

    const reviewCounts = [
        {
            label: "No reviews",
            value: 0,
        },
        {
            label: "From 1 to 3 reviews",
            value: 3,
        },
        {
            label: "From 4 to 6 reviews",
            value: 6,
        },
        {
            label: "From 7 to 9 reviews",
            value: 9,
        },
        {
            label: "More than 10 reviews",
            value: 11,
        },
    ];

    const experienceCounts = [
        {
            label: "Any",
            value: null,
        },
        {
            label: "1+ year",
            value: 1,
        },
        {
            label: "3+ years",
            value: 3,
        },
        {
            label: "6+ years",
            value: 6,
        },
    ]

    const currentSpecification = computed(() => {
        return specifications.find(item => item.documentId === filterForm.value.specificationId[0]);
    });

    const parentSpecification = computed(() => {
        if (!currentSpecification.value) return null;
        if (!currentSpecification.value.parent_id) return currentSpecification.value;
        return specifications.find(item => item.documentId === currentSpecification.value?.parent_id);
    });

    const specificationChildList = computed(() => {
        if (!parentSpecification.value?.documentId) return [];
        return globalStore.childSpecifications(parentSpecification.value.documentId);
    });

    async function getFilters() {
        if (!filterForm.value.specificationId[0]) return null;
        isLoaded.value = false;

        const response = await fetchData<PortfolioFilters>(endpoints.portfolio.filters, {
            method: 'GET',
            query: {
                specificationId: filterForm.value.specificationId[0]
            }
        }).then(({data}) => {
            if(data?.prices && Array.isArray(data.prices)){
                data.price = {
                    from: Math.min(...data.prices.map((p: any) => p.from)),
                    to: Math.max(...data.prices.map((p: any) => p.to))
                }
            }
            if(data) {
                data.reviews = reviewCounts
                data.experiences = experienceCounts
            }
            return data;
        }).catch(e => {
            console.error(e);
        }).finally(() => {
            isLoaded.value = true;
        });
        
        filtersList.value = response || null;
        
        setCityFilter()

        return filtersList.value;
    }

    function setCityFilter(){
        if(!route.params.city || route.params.city === 'all') return;
        const city = globalStore.locations.cities.find(item => item.name.toLowerCase().replace(/ /g, '-') === route.params.city)
        
        if(filtersList.value && city && filtersList.value?.cities.find(item => item.documentId === city?.documentId)){
            filterForm.value.city = [city?.documentId]
        }
    }

    const cleanedFilterForm = computed(() => {
        const form = { ...filterForm.value }

        return Object.entries(form).reduce((acc, [key, value]) => {
            // Пропускаем null, undefined и пустые массивы
            if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
                return acc
            }

            if (Array.isArray(value)) {
                (acc as any)[key] = value.join(',')
                return acc
            }

            (acc as any)[key] = value
            return acc
        }, {} as Record<string, any>)
    })

    async function getMasters() {
        if (!currentSpecification.value?.documentId) return [];
        //console.log(cleanedFilterForm.value)
        isLoaded.value = false;
        const response = await fetchData<PortfolioType[]>(endpoints.portfolio.search, {
            method: 'GET',
            query: cleanedFilterForm.value
        }).then(({data, meta}) => {
            if (meta?.pagination?.total) total.value = meta.pagination.total;
            return data;
        }).catch(e => {
            console.error(e);
        }).finally(() => {
            isLoaded.value = true;
        });

        mastersList.value = response?.filter(portfolio => !!portfolio.user) || []
        if(process.client){
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    return {
        isLoaded,
        filterForm,
        mastersList,
        total,
        filtersList,
        currentSpecification,
        parentSpecification,
        specificationChildList,
        cleanedFilterForm,
        getFilters,
        getMasters,
    };
}